import {
  Button,
  Card,
  Divider,
  Grid,
  Snackbar,
  Typography,
  IconButton,
  Alert as MuiAlert,
  Pagination,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Box
} from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import {
  Add as AddIcon,
  Delete,
  DeleteSweep,
  Search as SearchIcon,
  Star
} from '@mui/icons-material'
import React, { useEffect, useState, forwardRef, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck } from '../../utils/utils'
import ItemDrawer from '../menu_items/items/ItemDialog'
import _ from 'lodash'
import MainCard, {
  ConfirmationDialog,
  Search,
  SearchIconWrapper,
  SnackBarHideDuration,
  StyledInputBase,
  SnackBarTransition,
  ShowNoRecordsFound,
  ErrorScreen
} from '../../utils/ui_utils'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import {
  useCreateItems,
  useFetchItems,
  useSearchItems,
  useUpdateItems
} from '../../modules/items/itemsHooks'
import { useFetchUnits } from '../../modules/units/unitsHooks'
import { useFetchKitchenCounters } from '../../modules/kitchenCounters/kitchenCountersHooks'
import { FavoriteHelper } from '../../components/FavoriteComponents'
import axios from 'axios'
import { APIPATHENV } from '../../utils/tsUtils/Env'
import { getHeader } from '../../utils/tsUtils/utils'

function Items() {
  const jumpToInputRef = useRef(null)
  const searchInputRef = useRef(null)
  const [searchAutoFocus, setSearchAutoFocus] = useState(false)
  const [jumpAutoFocus, setJumpAutoFocus] = useState(false)
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [pagecount, setPageCount] = useState(1)
  const [goToPageInput, setGoToPageInput] = useState('')
  const [item, setItem] = useState(undefined)
  const [itemDrawerVisibility, setItemsDrawerVisibility] = useState(false)
  const [itemDrawerMode, setItemDrawerMode] = useState('edit')
  const [deleteConfirmationVisibility, setDeleteConfirmationVisibility] =
    useState(false)
  const [deleteAllConfirmVisibility, setDeleteAllConfirmVisibility] =
    useState(false)
  const [searchItemName, setSearchItemName] = useState('')
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [selectedOutletName, setSelectedOutletName] = useState('')
  const [itemsData, setItemsData] = useState([])
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [outlets, setOutlets] = useState([])
  const [enterPressed, setEnterPressed] = useState(false)

  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()
  const { fetchItems, fetchItemsData, fetchItemsError, fetchItemsLoading } =
    useFetchItems()
  const { createItems } = useCreateItems()
  const { searchItems, searchItemsData, searchItemsError, searchItemsLoading } =
    useSearchItems()
  const { updateItems } = useUpdateItems()
  const { unitsFetch, unitsFetchData } = useFetchUnits()
  const { kitchenCountersFetch, kitchenCountersData } =
    useFetchKitchenCounters()
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  // Deletes every item for the selected outlet. Items are the top of the
  // delete order, so this is always allowed by the backend.
  const handleDeleteAllItems = async () => {
    try {
      const response = await axios.post(
        `${APIPATHENV}hotel_billing/menu_items/delete_all`,
        { outlet_id: String(selectedOutlet) },
        { headers: getHeader() }
      )
      toastMessage(response?.data?.message || 'All items deleted', 'success')
      fetchItems({ outlet_id: selectedOutlet, pageNumber: 1, limit: limit })
    } catch (error) {
      const message =
        error?.response?.data?.message || 'Unable to delete. Please try again.'
      toastMessage(message, 'error')
    } finally {
      setDeleteAllConfirmVisibility(false)
    }
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    if (isSearchMode) {
      let payload = {
        outlet_id: selectedOutlet,
        pageNumber: value,
        limit: limit,
        searchQuery: searchItemName
      }
      searchItems(payload)
    } else {
      let payload = {
        outlet_id: selectedOutlet,
        pageNumber: value,
        limit: limit
      }
      fetchItems(payload)
    }
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (/^\d+$/.test(value) || value === '') {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNum = parseInt(goToPageInput, 10)
    if (pageNum >= 1 && pageNum <= pagecount) {
      handlePageChange(null, pageNum)
      setGoToPageInput('')
      // Trigger scroll to top
      setShouldScrollToTop(true)
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      handleGoToPageSubmit()
    }
  }

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  })

  const history = useNavigate()
  const pageCount = (data1) => {
    if (data1 == 0) {
      setPageCount(0)
    } else {
      let count = Math.ceil(data1 / limit)
      setPageCount(count)
    }
  }

  useEffect(() => {
    businessCheck(history)
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }, [])

  useEffect(() => {
    if (fetchItemsData.data?.length > 0) {
      setItemsData(fetchItemsData.data)
      setIsSearchMode(false)
      pageCount(fetchItemsData.total)
      // Scroll to top if triggered by Go button
      if (shouldScrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setShouldScrollToTop(false)
        setJumpAutoFocus(false)
        setSearchAutoFocus(true)
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 500)
      }
    } else {
      setItemsData([])
      setIsSearchMode(false)
      pageCount(0)
    }
  }, [fetchItemsData])

  useEffect(() => {
    if (searchItemsData.data?.length > 0) {
      setItemsData(searchItemsData.data)
      setIsSearchMode(true)
      pageCount(searchItemsData.total)
      // Scroll to top if triggered by Go button
      if (shouldScrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        setShouldScrollToTop(false)
        setJumpAutoFocus(false)
        setSearchAutoFocus(true)
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus()
          }
        }, 500)
      }
    } else {
      pageCount(0)
    }
  }, [searchItemsData.data])

  useEffect(() => {
    if (outletFetchData.data?.length > 0) {
      setOutlets(outletFetchData.data)
      setSelectedOutlet(outletFetchData.data[0]._id)
      setSelectedOutletName(outletFetchData.data[0].name)
      let payload = {
        outlet_id: outletFetchData.data[0]._id,
        pageNumber: 1,
        limit: 1000
      }
      kitchenCountersFetch(payload)
      let payload2 = _.cloneDeep(payload)
      payload2.limit = limit
      fetchItems(payload2)
      unitsFetch(outletFetchData.data[0]._id)
    } else {
      setOutlets([])
      setSelectedOutlet('')
      setSelectedOutletName('')
    }
  }, [outletFetchData])

  // useEffect(() => {
  //   if (!searchItemName && isSearchMode) {
  //     setPage(1)
  //     setIsSearchMode(false)
  //     setEnterPressed(false)
  //     if (selectedOutlet != null && selectedOutlet != undefined && selectedOutlet != '')
  //       itemsAPI.fetch.api(selectedOutlet, 1, limit)
  //   } else {
  //     const delayDebounceFn = setTimeout(() => {
  //       setPage(1);
  //       setIsSearchMode(true);
  //       if (selectedOutlet && !enterPressed) {
  //         itemsAPI.search.api(selectedOutlet, 1, limit, searchItemName);
  //       }
  //     }, 3000)
  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchItemName]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchItemName) {
      setPage(1)
      setIsSearchMode(true)
      setEnterPressed(true)
      if (selectedOutlet) {
        let payload = {
          outlet_id: selectedOutlet,
          pageNumber: 1,
          limit: limit,
          searchQuery: searchItemName
        }
        searchItems(payload)
      }
    }
  }

  const handleChange = (e) => {
    setSearchItemName(e.target.value)
    setEnterPressed(false)
    if (e.target.value == '') {
      setPage(1)
      setIsSearchMode(false)
      setEnterPressed(false)
      if (
        selectedOutlet != null &&
        selectedOutlet != undefined &&
        selectedOutlet != ''
      ) {
        let payload = {
          outlet_id: selectedOutlet,
          pageNumber: 1,
          limit: limit
        }
        fetchItems(payload)
      }
    }
  }
  const fetchOutletOnError = () => {
    let payload = {
      pageNumber: 1,
      limit: 1000
    }
    outletFetch(payload)
  }
  const handleOutletChange = (event) => {
    setItemsData([])
    setSelectedOutlet(event.target.value)

    outlets.map((outlet) => {
      if (outlet._id == event.target.value) {
        setSelectedOutletName(outlet.name)
        return
      }
    })

    fetchItemsAndKitchenCountersOnOutletChange(event.target.value)
  }

  const fetchItemsAndKitchenCountersOnOutletChange = (outletId) => {
    setPage(1)
    let payload = {
      outlet_id: outletId,
      pageNumber: 1,
      limit: 1000
    }
    kitchenCountersFetch(payload)
    let payload2 = _.cloneDeep(payload)
    payload2.limit = limit
    fetchItems(payload2)
    unitsFetch(outletId)
  }

  const ShowItemsDetailsWithAccordion = () => (
    <Grid container justifyContent="center" spacing={10} sx={{ paddingTop: 5 }}>
      <Grid item sm={8} md={10}>
        <Card raised sx={{ borderRadius: 3 }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Stack spacing={3}>
              <MainCard sx={{ width: 250 }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase
                    inputRef={searchInputRef}
                    placeholder="Search Item by Name or SKU"
                    inputProps={{
                      'aria-label': 'search'
                    }}
                    autoFocus={searchAutoFocus}
                    value={searchItemName}
                    onFocus={(e) => {
                      setSearchAutoFocus(true)
                      setJumpAutoFocus(false)
                      const scrollPos = window.pageYOffset
                      requestAnimationFrame(() => {
                        window.scrollTo(0, scrollPos)
                      })
                    }}
                    onKeyDown={(e) => {
                      const scrollPos = window.pageYOffset
                      e.stopPropagation()
                      handleKeyDown(e)
                      requestAnimationFrame(() => {
                        window.scrollTo(0, scrollPos)
                      })
                    }}
                    onChange={(e) => {
                      const scrollPos = window.pageYOffset
                      e.stopPropagation()
                      handleChange(e)
                      window.scrollTo(0, scrollPos)
                    }}
                    onInput={(e) => {
                      const scrollPos = window.pageYOffset
                      requestAnimationFrame(() => {
                        window.scrollTo(0, scrollPos)
                      })
                    }}
                  />
                </Search>
              </MainCard>
              {!outletFetchLoading &&
                !fetchItemsLoading &&
                itemsData &&
                itemsData.length > 0 &&
                itemsData.map((rows, index) => {
                  let row = _.cloneDeep(rows)
                  row.kitchen_counter_id = row.kitchen_counter?._id
                  const isFavorite = row.is_favorite || false
                  const priority = row.priority || 0
                  const priorityColor = isFavorite ? FavoriteHelper.getPriorityColor(priority) : '#9e9e9e'

                  return (
                    <MainCard
                      key={index}
                      sx={{
                        mt: 1,
                        backgroundColor:
                          index % 2 == 0 ? 'background.default' : 'white',
                        p: 2,
                        alignItems: 'center',
                        transition: 'box-shadow 0.3s ease',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                          backgroundColor: 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <Grid container column={6} spacing={1}>
                        <Grid item xs md>
                          <Grid
                            container
                            spacing={2}
                            p={5}
                            onClick={() => {
                              setItemDrawerMode('edit')
                              setItemsDrawerVisibility(true)
                              setItem(row)
                            }}
                          >
                            <Grid item xs={12} sm={12} md={0.5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: 40,
                                  minHeight: 40
                                }}
                              >
                                <Star
                                  sx={{
                                    color: isFavorite ? priorityColor : '#e0e0e0',
                                    fontSize: 40
                                  }}
                                />
                                {isFavorite && (
                                  <Typography
                                    sx={{
                                      position: 'absolute',
                                      fontSize: 14,
                                      fontWeight: 'bold',
                                      color: '#fff',
                                      top: '60%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      lineHeight: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {priority}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={12} md={4}>
                              <Grid container direction="column">
                                <Typography variant="h4" sx={{ mb: 0.5 }}>
                                  {row.name}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  in {row.kitchen_counter?.name}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs sm md={2} textAlign="center">
                              <Grid container direction="column">
                                <Typography
                                  sx={{ mb: 0.5, color: 'text.secondary' }}
                                >
                                  SKU{' '}
                                </Typography>
                                <Typography variant="h5">
                                  {row.sku || '-'}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs sm md={1.5} textAlign="right">
                              <Grid container direction="column">
                                <Typography
                                  sx={{ mb: 0.5, color: 'text.secondary' }}
                                >
                                  Price 1{' '}
                                </Typography>
                                <Typography variant="h5">
                                  {row.price[0]?.unit_price}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs sm md={1.5} textAlign="right">
                              <Grid container direction="column">
                                <Typography
                                  sx={{ mb: 0.5, color: 'text.secondary' }}
                                >
                                  Price 2{' '}
                                </Typography>
                                <Typography variant="h5">
                                  {row.price[1]?.unit_price}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid item xs sm md={1.5} textAlign="right">
                              <Grid container direction="column">
                                <Typography
                                  sx={{ mb: 0.5, color: 'text.secondary' }}
                                >
                                  Price 3{' '}
                                </Typography>
                                <Typography variant="h5">
                                  {row.price[2]?.unit_price}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          xs
                          md={0.5}
                          sm
                          sx={{ display: 'flex', alignItems: 'center' }}
                          textAlign="right"
                        >
                          <IconButton
                            size="small"
                            color="lightRed"
                            component="span"
                            onClick={() => {
                              row.kitchen_counter_id = row?.kitchen_counter?._id
                              setItem(row)
                              setDeleteConfirmationVisibility(true)
                            }}
                          >
                            <Delete fontSize="medium" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </MainCard>
                  )
                })}
            </Stack>
          </CardContent>
          {pagecount > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: 'grey.50',
                borderRadius: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%'
                }}
              >
                <Pagination
                  count={pagecount}
                  page={page}
                  color="secondary"
                  size="small"
                  boundaryCount={2}
                  siblingCount={0}
                  showFirstButton
                  showLastButton
                  onChange={handlePageChange}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      transition: 'all 0.2s ease-in-out'
                    }
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                  backgroundColor: 'white',
                  p: 2,
                  borderRadius: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontWeight: 500 }}
                >
                  Jump to
                </Typography>
                <TextField
                  inputRef={jumpToInputRef}
                  size="small"
                  autoFocus={jumpAutoFocus}
                  value={goToPageInput}
                  onFocus={(e) => {
                    setJumpAutoFocus(true)
                    setSearchAutoFocus(false)
                    const scrollPos = window.pageYOffset
                    requestAnimationFrame(() => {
                      window.scrollTo(0, scrollPos)
                    })
                  }}
                  onChange={(e) => {
                    const scrollPos = window.pageYOffset
                    e.stopPropagation()
                    handleGoToPageChange(e)
                    window.scrollTo(0, scrollPos)
                  }}
                  onKeyPress={(e) => {
                    const scrollPos = window.pageYOffset
                    e.stopPropagation()
                    handleGoToPageKeyPress(e)
                    window.scrollTo(0, scrollPos)
                  }}
                  placeholder="Page"
                  onKeyDown={(e) => {
                    const scrollPos = window.pageYOffset
                    e.stopPropagation()
                    if (
                      [
                        'ArrowUp',
                        'ArrowDown',
                        'PageUp',
                        'PageDown',
                        'Home',
                        'End'
                      ].includes(e.key)
                    ) {
                      e.preventDefault()
                    }
                    requestAnimationFrame(() => {
                      window.scrollTo(0, scrollPos)
                    })
                  }}
                  onInput={(e) => {
                    const scrollPos = window.pageYOffset
                    requestAnimationFrame(() => {
                      window.scrollTo(0, scrollPos)
                    })
                  }}
                  inputProps={{
                    style: { textAlign: 'center' }
                  }}
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      transition: 'all 0.2s ease-in-out',
                      '& fieldset': {
                        borderColor: 'grey.300',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '1.5px'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'secondary.main',
                        borderWidth: '2px',
                        boxShadow: '0 0 0 3px rgba(156, 39, 176, 0.1)'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      textAlign: 'center',
                      fontWeight: 500,
                      padding: '7px 8px',
                      height: '20px',
                      lineHeight: '20px'
                    }
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontWeight: 400 }}
                >
                  of {pagecount}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault()
                    handleGoToPageSubmit()
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  disableFocusRipple
                  disabled={
                    !goToPageInput ||
                    parseInt(goToPageInput, 10) < 1 ||
                    parseInt(goToPageInput, 10) > pagecount
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 0.75,
                    height: '34px',
                    lineHeight: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    }
                  }}
                >
                  Go
                </Button>
              </Box>
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  )

  const ShowItemsSimmer = () => (
    <Grid container justifyContent="center" spacing={10} sx={{ paddingTop: 5 }}>
      <Grid item sm={8} md={6}>
        <Card raised sx={{ borderRadius: 3 }}>
          <CardContent sx={{ flexGrow: 1 }}></CardContent>
          <Stack spacing={3}>
            <Grid container sx={{ ml: 5 }}>
              <Grid item xs sm md>
                <Grid container direction="column">
                  <Typography
                    variant="h4"
                    justifyContent={'center'}
                    sx={{ mb: 0.5 }}
                  >
                    <Skeleton
                      animation="wave"
                      variant="rectangular"
                      sx={{
                        width: { xs: 50, sm: 50, md: 500 },
                        height: 50
                      }}
                    />
                  </Typography>
                  <Grid
                    container
                    justifyContent="center"
                    sx={{ paddingTop: 5, ml: 8 }}
                  >
                    <Grid item sm md>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          width={200}
                          height={25}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-arround"
                    sx={{ paddingTop: 3, ml: 8 }}
                  >
                    <Grid item sm={3} md={3}>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          width={200}
                          height={25}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-arround"
                    sx={{ paddingTop: 3, ml: 8 }}
                  >
                    <Grid item sm={3} md={3}>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          width={200}
                          height={25}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-arround"
                    sx={{ paddingTop: 3, ml: 8 }}
                  >
                    <Grid item sm={3} md={3}>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          width={200}
                          height={25}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-arround"
                    sx={{ paddingTop: 3, ml: 8, mb: 5 }}
                  >
                    <Grid item sm md>
                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                        <Skeleton
                          animation="wave"
                          variant="rounded"
                          width={200}
                          height={25}
                        />
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  )
  return (
    <>
      <Helmet title="Items" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item xs={9}>
          <Typography variant="h4" gutterBottom display="inline">
            Items
          </Typography>
        </Grid>
        {!outletFetchError && (
          <Grid item>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                if (
                  selectedOutlet != null &&
                  selectedOutlet != undefined &&
                  selectedOutlet != ''
                ) {
                  setItemsDrawerVisibility(true)
                  setItem(undefined)
                  setItemDrawerMode('new')
                } else {
                  toastMessage('Select Outlet', 'warning')
                }
              }}
            >
              Add Item
            </Button>
            <Button
              startIcon={<DeleteSweep />}
              variant="contained"
              size="small"
              color="error"
              sx={{ ml: 1 }}
              onClick={() => {
                if (
                  selectedOutlet != null &&
                  selectedOutlet != undefined &&
                  selectedOutlet != ''
                ) {
                  setDeleteAllConfirmVisibility(true)
                } else {
                  toastMessage('Select Outlet', 'warning')
                }
              }}
            >
              Delete All
            </Button>

            <ItemDrawer
              outlet={selectedOutletName}
              visible={itemDrawerVisibility}
              title={itemDrawerMode == 'new' ? 'Create Item' : 'Edit Item'}
              okText={itemDrawerMode == 'new' ? 'Create' : 'Update'}
              mode={itemDrawerMode}
              initialValue={item}
              kitchenCounterData={kitchenCountersData?.data}
              unitsData={unitsFetchData?.data ?? []}
              onClose={() => setItemsDrawerVisibility(false)}
              onSubmit={(values) => {
                let Value = _.cloneDeep(values)
                if (Value.parcel_amt != '') Value.parcel_amt = +Value.parcel_amt
                else Value.parcel_amt = 0
                Value.price[0].cost_price =
                  Value.price[0].cost_price !== ''
                    ? +Value.price[0].cost_price
                    : 0
                Value.price[0].cess1 =
                  Value.price[0].cess1 !== '' ? +Value.price[0].cess1 : 0
                Value.price[0].cess2 =
                  Value.price[0].cess2 !== '' ? +Value.price[0].cess2 : 0
                Value.price[1].cost_price = Value.price[0].cost_price
                Value.price[1].cess1 = Value.price[0].cess1
                Value.price[1].cess2 = Value.price[0].cess2
                Value.price[2].cost_price = Value.price[0].cost_price
                Value.price[2].cess1 = Value.price[0].cess1
                Value.price[2].cess2 = Value.price[0].cess2

                for (let i = 0; i < 3; i++) {
                  Value.price[i].cgst =
                    Value.price[i].cgst != '' ? Value.price[i].cgst : 0
                  Value.price[i].sgst =
                    Value.price[i].sgst != '' ? Value.price[i].sgst : 0
                  Value.price[i].unit_price =
                    Value.price[i].unit_price != ''
                      ? +Value.price[i].unit_price
                      : 0
                }

                // Handle favorite fields
                Value.is_favorite = Value.is_favorite || false
                Value.priority = Value.is_favorite ? (Value.priority || 0) : 0

                delete Value.category_id
                delete Value.sub_category_id
                delete Value.stock_balance
                if (itemDrawerMode == 'new') {
                  Value.outlet_id = selectedOutlet
                  Value.kitchen_counter_id = BigInt(Value.kitchen_counter_id)
                  createItems(Value, (success, error, message) => {
                    if (error) {
                      let errorMessage =
                        message || 'Error while creating Item. Try again'
                      setItemsDrawerVisibility(true)
                      toastMessage(errorMessage, 'error')
                    } else {
                      setItemsDrawerVisibility(false)
                      toastMessage('Item created', 'success')
                      let payload = {
                        outlet_id: selectedOutlet,
                        pageNumber: page,
                        limit: limit
                      }
                      fetchItems(payload)
                    }
                  })
                } else {
                  Value.id = Value._id
                  delete Value._id
                  delete Value.business_id
                  delete Value.timestamp
                  delete Value.kitchen_counter
                  delete Value.category_info
                  delete Value.sub_category_info
                  delete Value.sub_category_id
                  delete Value.stock_balance
                  delete Value.key
                  Value.kitchen_counter_id = BigInt(Value.kitchen_counter_id)
                  for (let i = 0; i < Value.price.length; i++) {
                    Value.price[i].id = Value.price[i]._id
                    delete Value.price[i]._id
                  }
                  updateItems(Value, (success, error, message) => {
                    if (error) {
                      let errorMessage =
                        message || 'Error while updating Item. Try again'
                      setItemsDrawerVisibility(true)
                      toastMessage(errorMessage, 'error')
                    } else {
                      setItemsDrawerVisibility(false)
                      toastMessage('Item updated', 'success')
                      let payload = {
                        outlet_id: selectedOutlet,
                        pageNumber: page,
                        limit: limit
                      }
                      fetchItems(payload)
                    }
                  })
                }
              }}
            />
          </Grid>
        )}
      </Grid>
      <Divider sx={{ marginTop: 5 }} />
      {outletFetchError ? (
        <ErrorScreen
          onClick={() => {
            fetchOutletOnError()
          }}
        />
      ) : (
        <div>
          {' '}
          <Grid container spacing={6} style={{ paddingTop: 10 }}>
            <Grid item md={3}>
              <TextField
                size="small"
                label="Outlet"
                id="outlet"
                name="outlet"
                variant="outlined"
                margin="dense"
                value={selectedOutlet}
                onChange={handleOutletChange}
                select
                fullWidth
              >
                {outlets.map((outlet) => (
                  <MenuItem key={outlet._id} value={outlet._id}>
                    {outlet.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          {!isSearchMode ? (
            <>
              {fetchItemsError ? (
                <ErrorScreen
                  onClick={() => {
                    fetchItemsAndKitchenCountersOnOutletChange(selectedOutlet)
                  }}
                />
              ) : (
                <>
                  {(outletFetchLoading || fetchItemsLoading) && (
                    <ShowItemsSimmer />
                  )}
                  {!fetchItemsLoading && itemsData && itemsData.length > 0 && (
                    <ShowItemsDetailsWithAccordion />
                  )}
                  {!fetchItemsLoading && !Object.keys(itemsData)?.length && (
                    <ShowNoRecordsFound />
                  )}
                </>
              )}
            </>
          ) : (
            <ShowItemsDetailsWithAccordion />
          )}
        </div>
      )}
      <div>
        <ConfirmationDialog
          visibility={deleteAllConfirmVisibility}
          title={'Delete ALL Items'}
          message={
            'This will delete ALL items for this outlet. Are you sure you want to continue?'
          }
          okText="Delete All"
          cancelText={'Cancel'}
          onCancel={() => {
            setDeleteAllConfirmVisibility(false)
          }}
          onOk={() => {
            handleDeleteAllItems()
          }}
        />
        <ConfirmationDialog
          visibility={deleteConfirmationVisibility}
          title={'Delete Item'}
          message={`Do you want to delete Item - ${item?.name} ?`}
          okText="Delete"
          cancelText={'Cancel'}
          onCancel={() => {
            setDeleteConfirmationVisibility(false)
          }}
          onOk={() => {
            let Value = _.cloneDeep(item)
            Value.delete_status = true
            for (let i = 0; i < Value.price.length; i++) {
              Value.price[i].id = Value.price[i]._id
              delete Value.price[i]._id
            }
            Value.id = Value._id
            delete Value._id
            delete Value.business_id
            delete Value.timestamp
            delete Value.kitchen_counter
            delete Value.category_info
            delete Value.sub_category_info
            delete Value.sub_category_id
            delete Value.itemlist
            delete Value.stock_balance
            delete Value.key
            updateItems(Value, (success, error, message) => {
              if (error) {
                let errorMessage =
                  message || 'Error while deleting Item. Try again'
                setDeleteConfirmationVisibility(false)
                toastMessage(errorMessage, 'error')
              } else {
                setDeleteConfirmationVisibility(false)
                toastMessage('Item deleted', 'success')
                if (itemsData.length - 1 >= 1) {
                  let payload = {
                    outlet_id: selectedOutlet,
                    pageNumber: page,
                    limit: limit
                  }
                  fetchItems(payload)
                } else {
                  setPage(1)
                  let payload = {
                    outlet_id: selectedOutlet,
                    pageNumber: 1,
                    limit: limit
                  }
                  fetchItems(payload)
                }
              }
            })
          }}
        />
      </div>
      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>
    </>
  )
}

export default Items
